import { pushEscapeLayer } from '../composables/useEscape'

// Lightbox галереи. Отдельным бандлом, потому что CSP (script-src 'self')
// режет инлайн-скрипты — вьюха подключает его через vite_typescript_tag.
export function initGallery(): void {
  // Плитка лайтбокса опознаётся атрибутом, а не классом: тот же слой открывается
  // и на лендинге (.gal-item), и на странице зала (.hl__shot-btn).
  const items = Array.from(document.querySelectorAll<HTMLElement>('[data-lightbox]'))
  // Ранний выход гасит заодно и раскрытие хвоста ниже: страница с кнопкой
  // [data-gallery-more], но без единой плитки [data-lightbox], осталась бы с
  // мёртвой кнопкой. На сегодня такой разметки нет — обе страницы ставят кнопку
  // только когда кадры уже есть, — но связь неочевидна при чтении файла по частям.
  if (items.length === 0) return

  const box = document.createElement('div')
  box.className = 'lightbox'
  box.hidden = true
  box.setAttribute('role', 'dialog')
  box.setAttribute('aria-modal', 'true')
  // Имя статичное, а не aria-labelledby на figcaption: подпись заполняется при
  // открытии конкретного кадра, а имя диалог обязан нести уже в разметке — иначе
  // диктор объявляет безымянный диалог.
  box.setAttribute('aria-label', 'Просмотр фотографии')
  box.innerHTML = `
    <button type="button" class="lightbox-close" aria-label="Закрыть">×</button>
    <button type="button" class="lightbox-nav lightbox-prev" aria-label="Предыдущее фото">‹</button>
    <button type="button" class="lightbox-nav lightbox-next" aria-label="Следующее фото">›</button>
    <figure><img alt=""><figcaption class="lightbox-caption"></figcaption></figure>`
  document.body.appendChild(box)

  const image = box.querySelector('img') as HTMLImageElement
  const figure = box.querySelector('figure') as HTMLElement
  const caption = box.querySelector('.lightbox-caption') as HTMLElement
  const closeButton = box.querySelector('.lightbox-close') as HTMLButtonElement
  const prevButton = box.querySelector('.lightbox-prev') as HTMLButtonElement
  const nextButton = box.querySelector('.lightbox-next') as HTMLButtonElement
  // Один кадр листать некуда — стрелки убираем совсем, а не показываем неактивными.
  if (items.length < 2) {
    prevButton.hidden = true
    nextButton.hidden = true
  }

  // Индекс открытого кадра. -1 — слой закрыт. Раньше здесь лежал сам элемент
  // (opener), но с листанием «где мы сейчас» перестало совпадать с «откуда открыли».
  let current = -1
  // Номер открытия растёт с каждым кликом. Пока новый кадр не декодирован, картинка
  // погашена классом is-loading — иначе секунду виден предыдущий снимок. Тот же класс
  // на figure включает спиннер: без него на медленной сети посетитель секундами видит
  // тёмный слой с крестиком и пустотой и не понимает, открылось что-то или сломалось.
  // Классом, а не через :has() у CSS, — состояние проверяется юнит-тестом в jsdom и не
  // зависит от поддержки селектора. Снимать
  // класс имеет право только то открытие, чей номер ещё актуален: открыли B, пока
  // декодировался A, — поздний ответ A не должен показывать чужой кадр. Сравниваем
  // именно номер, а не image.src: jsdom приводит src к абсолютному URL, и сравнение
  // строк соврало бы.
  let openToken = 0
  // Слой реестра Escape (composables/useEscape): живёт ровно пока лайтбокс открыт —
  // ставится при первом открытии (wasClosed в openAt), снимается в close().
  let removeEscapeLayer: (() => void) | null = null

  // Предзагрузка полного кадра по наведению: к моменту клика он обычно уже в кеше
  // браузера, и decode проходит мгновенно. Set держит по одному запросу на плитку —
  // курсор ходит по сетке много раз.
  const preloaded = new Set<string>()
  function preload(item: HTMLElement): void {
    const full = item.dataset.full
    if (!full || preloaded.has(full)) return

    preloaded.add(full)
    new Image().src = full
  }

  // Сворачивание хвоста галереи. Опознаётся по атрибутам, а не по классам лендинга:
  // ту же механику использует страница зала со своей сеткой .hl__shots.
  const more = document.querySelector<HTMLButtonElement>('[data-gallery-more]')
  const grid = more && document.getElementById(more.getAttribute('aria-controls') ?? '')

  // Раскрытие одностороннее: обратно галерею никто не сворачивает, поэтому кнопка
  // после клика уходит со страницы.
  function expandTail(): void {
    if (!grid?.classList.contains('is-collapsed')) return

    grid.classList.remove('is-collapsed')
    if (more) {
      more.setAttribute('aria-expanded', 'true')
      more.hidden = true
    }
  }

  async function openAt(index: number): Promise<void> {
    // Кольцо: с последнего кадра вперёд попадаем на первый. На десятке снимков это
    // приятнее, чем упереться в тупик и не понять, кончилась галерея или сломалась.
    current = (index + items.length) % items.length
    const item = items[current]
    const token = ++openToken
    image.classList.add('is-loading')
    figure.classList.add('is-loading')
    image.src = item.dataset.full ?? ''
    image.alt = item.dataset.alt ?? ''
    caption.textContent = item.dataset.alt ?? ''

    // Фокус на «закрыть» переводим только при первом открытии: при листании он
    // уже там, а повторный focus сбивал бы чтение экранным диктором.
    const wasClosed = box.hidden
    box.hidden = false
    if (wasClosed) {
      closeButton.focus()
      removeEscapeLayer = pushEscapeLayer(close)
    }

    // Соседей подгружаем заранее и без выдержки: сосед уже открытого кадра нужен
    // сейчас, а не по таймеру — листают обычно подряд.
    preload(items[(current + 1) % items.length])
    preload(items[(current - 1 + items.length) % items.length])

    // decode() в jsdom не реализован — там показываем кадр сразу.
    if (typeof image.decode === 'function') {
      try {
        await image.decode()
      } catch {
        // decode отклоняется на битом или прерванном кадре. Показываем всё равно:
        // иконка сломанной картинки лучше, чем навсегда пустая модалка.
      }
    }
    if (token === openToken) {
      image.classList.remove('is-loading')
      figure.classList.remove('is-loading')
    }
  }

  // Фокус возвращается на плитку ТЕКУЩЕГО кадра: иначе клавиатурный посетитель после
  // закрытия оказывается там, откуда начал листать, а не там, где закончил.
  // src и is-loading здесь намеренно не сбрасываются: следующий openAt() перезатирает
  // и то и другое до показа слоя, а очистка src дала бы лишний запрос на пустой адрес.
  function close(): void {
    removeEscapeLayer?.()
    removeEscapeLayer = null
    box.hidden = true
    const item = items[current]
    // Долистать можно и в свёрнутый хвост галереи: массив плиток собран при
    // инициализации и содержит скрытые. Раскрываем сетку до возврата фокуса, иначе
    // фокус уходит на элемент с display:none и теряется. Признак — атрибут, а не
    // offsetParent: в jsdom он всегда пуст, и тест на нём не написать.
    if (item?.hasAttribute('data-gallery-extra')) expandTail()
    item?.focus()
    current = -1
  }

  // Выдержка перед предзагрузкой (hover-intent). Без неё проезд курсора по сетке к
  // футеру дёргал полные кадры всех пройденных плиток — на проде это до 16 файлов
  // 1600x1200 у человека, который лайтбокс вообще не откроет. Таймер свой на каждую
  // плитку: курсор уходит с одной, но может стоять на другой.
  const PRELOAD_DELAY_MS = 140
  const timers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>()

  // Предзагрузки по focus нет намеренно: табом сетку чаще проходят насквозь, чем
  // открывают, а пустоту в модалке при открытии теперь закрывает спиннер.
  items.forEach((item, index) => {
    item.addEventListener('click', () => void openAt(index))
    item.addEventListener('pointerenter', () => {
      timers.set(item, setTimeout(() => preload(item), PRELOAD_DELAY_MS))
    })
    item.addEventListener('pointerleave', () => {
      const timer = timers.get(item)
      if (timer !== undefined) clearTimeout(timer)
      timers.delete(item)
    })
  })
  prevButton.addEventListener('click', () => void openAt(current - 1))
  nextButton.addEventListener('click', () => void openAt(current + 1))
  closeButton.addEventListener('click', close)

  // Порог 40px отсекает дрожание пальца при обычном тапе.
  const SWIPE_MIN = 40
  let pointerStartX = 0
  let swiped = false

  box.addEventListener('pointerdown', (event) => {
    pointerStartX = event.clientX
    // Флаг гасим на каждом нажатии, а не только в click: за pointerup клик приходит
    // не всегда (pointercancel, мультитач, перехваченный жест), и доживший до
    // следующего раза флаг съел бы один настоящий клик по подложке.
    swiped = false
  })
  box.addEventListener('pointerup', (event) => {
    // Единственный кадр листать некуда: свайп переоткрывал бы его же — с полным
    // циклом is-loading, то есть с миганием.
    if (items.length < 2) return
    // Свайп, закончившийся на стрелке, отдаём самой стрелке: иначе кадр листается
    // дважды — сперва здесь, потом на click кнопки.
    if ((event.target as HTMLElement).closest('.lightbox-nav')) return

    const dx = event.clientX - pointerStartX
    swiped = Math.abs(dx) >= SWIPE_MIN
    if (swiped) void openAt(dx < 0 ? current + 1 : current - 1)
  })
  // Флаг нужен потому, что свайп, закончившийся на подложке, породил бы ещё и click
  // с target === box — то есть закрыл бы слой сразу после перелистывания.
  box.addEventListener('click', (event) => {
    if (swiped) {
      swiped = false
      return
    }
    if (event.target === box) close()
  })
  document.addEventListener('keydown', (event) => {
    if (box.hidden) return

    if (event.key === 'Tab') {
      // aria-modal="true" обещает модальность, которой без ловушки нет: Tab уводил
      // фокус на галерею под слоем. Кольцо — только видимые кнопки: на единственном
      // кадре стрелки скрыты через hidden и в обход не входят.
      const ring = [closeButton, prevButton, nextButton].filter((button) => !button.hidden)
      event.preventDefault()
      // Фокус вне кольца (браузер успел увести раньше) возвращаем на первую кнопку.
      const index = ring.indexOf(document.activeElement as HTMLButtonElement)
      const step = event.shiftKey ? -1 : 1
      ring[index === -1 ? 0 : (index + step + ring.length) % ring.length]?.focus()
      return
    }

    if (items.length < 2) return

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      // Скролл-лока у фона нет, поэтому стрелки надо гасить: иначе страница едет под
      // слоем, и после закрытия посетитель стоит не там, откуда открывал.
      event.preventDefault()
      void openAt(event.key === 'ArrowRight' ? current + 1 : current - 1)
    }
  })

  more?.addEventListener('click', () => {
    expandTail()
    // Фокус — на первый открывшийся кадр: без этого посетитель с клавиатуры после
    // исчезновения кнопки оказывается в начале документа. Ищем именно плитку
    // ([data-lightbox]), а не любой носитель data-gallery-extra: на странице зала
    // атрибут стоит и на <li> (ради CSS-правила, прячущего хвост), и на кнопке
    // внутри него — querySelector без уточнения нашёл бы нефокусируемый <li> первым.
    // Ищем в пределах grid, а не всего документа — на странице может быть не одна галерея.
    grid?.querySelector<HTMLElement>('[data-lightbox][data-gallery-extra]')?.focus()
  })
}
